import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  createChart,
  type CandlestickData,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type MouseEventParams,
  type Time,
} from 'lightweight-charts';
import { formatCurrency } from '@shared/SharedAccountBody';
import { DataCard } from '@/components/nq/DataCard';
import {
  DOLLAR_PRICE_FORMAT,
  PCT_PRICE_FORMAT,
  TV_CHROME,
  TvRow,
  TvTooltipCard,
  fmtTvDate,
  toPeriodReturnCandles,
  toPeriodReturnLine,
  tvThemeColors,
  type TvCandle,
  type TvLine,
} from './tvShared';

type Mode = '$' | '%';

interface HoverBar {
  time: Time;
  close: number;
  bench?: number;
  x: number;
  y: number;
}

interface PortfolioCandlesChartProps {
  candles: TvCandle[];
  benchmark: TvLine[];
  benchmarkAvailable?: boolean;
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  loading?: boolean;
  error?: string | null;
}

const CHART_HEIGHT = 340;

const toneClass = (v: number | undefined) =>
  v === undefined
    ? 'text-muted-foreground'
    : v >= 0
      ? 'text-[color:var(--nq-up-soft)]'
      : 'text-[color:var(--nq-down-bright)]';
const fmtPct = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}%`;

const PortfolioCandlesChart = ({
  candles,
  benchmark,
  benchmarkAvailable = false,
  mode,
  onModeChange,
  loading,
  error,
}: PortfolioCandlesChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lineRef = useRef<ISeriesApi<'Line'> | null>(null);
  const [hover, setHover] = useState<HoverBar | null>(null);

  const hasData = candles.length > 0;
  // Period-over-period returns: each bar rebased to the prior bar's close, so a
  // bar's % is that period's own move (the last bar matches "Today's results"),
  // not a running total. Drives %-mode bars and the tooltip in both modes.
  const pctCandles = useMemo(() => toPeriodReturnCandles(candles), [candles]);
  const pctBenchmark = useMemo(() => toPeriodReturnLine(benchmark), [benchmark]);
  const returnByTime = useMemo(() => {
    const m = new Map<string, { port: number; bench?: number }>();
    pctCandles.forEach((c) => m.set(String(c.time), { port: c.close }));
    pctBenchmark.forEach((p) => {
      const key = String(p.time);
      m.set(key, { port: m.get(key)?.port ?? 0, bench: p.value });
    });
    return m;
  }, [pctCandles, pctBenchmark]);

  // Create the chart + series once; the container stays mounted so this runs
  // before any data arrives.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const colors = tvThemeColors();
    const chart = createChart(el, {
      width: el.clientWidth,
      height: CHART_HEIGHT,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: TV_CHROME.textColor,
        fontFamily: TV_CHROME.fontFamily,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: TV_CHROME.vertGrid },
        horzLines: { color: TV_CHROME.horzGrid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: TV_CHROME.border },
      timeScale: { borderColor: TV_CHROME.border, timeVisible: false },
    });

    const candle = chart.addSeries(CandlestickSeries, {
      upColor: colors.up,
      downColor: colors.down,
      borderUpColor: colors.up,
      borderDownColor: colors.down,
      wickUpColor: colors.up,
      wickDownColor: colors.down,
    });
    const line = chart.addSeries(LineSeries, {
      color: colors.bench,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
    });

    chartRef.current = chart;
    candleRef.current = candle;
    lineRef.current = line;

    const handleCrosshair = (param: MouseEventParams) => {
      const cd = param.seriesData.get(candle) as CandlestickData | undefined;
      const ld = param.seriesData.get(line) as LineData | undefined;
      if (!param.time || !param.point || !cd || cd.close === undefined) {
        setHover(null);
        return;
      }
      setHover({
        time: param.time,
        close: cd.close,
        bench: ld?.value,
        x: param.point.x,
        y: param.point.y,
      });
    };
    chart.subscribeCrosshairMove(handleCrosshair);

    const resizeObserver = new ResizeObserver(() => {
      chart.applyOptions({ width: el.clientWidth });
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      chart.unsubscribeCrosshairMove(handleCrosshair);
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      lineRef.current = null;
      setHover(null);
    };
  }, []);

  // Push data whenever candles / benchmark / mode change.
  useEffect(() => {
    const candle = candleRef.current;
    const line = lineRef.current;
    const chart = chartRef.current;
    if (!candle || !line || !chart) return;

    candle.applyOptions({ priceFormat: mode === '%' ? PCT_PRICE_FORMAT : DOLLAR_PRICE_FORMAT });
    candle.setData((mode === '%' ? pctCandles : candles) as CandlestickData[]);
    line.setData((mode === '%' ? pctBenchmark : benchmark) as LineData[]);
    chart.timeScale().fitContent();
    setHover(null);
  }, [candles, benchmark, mode, pctCandles, pctBenchmark]);

  // Tooltip shows each bar's own period return (same value in $ and % modes),
  // looked up by the hovered bar's time.
  const hReturn = hover ? returnByTime.get(String(hover.time)) : undefined;
  const hPct = hReturn?.port ?? 0;
  const hBenchPct = hReturn?.bench;
  const hAlpha = hBenchPct !== undefined ? hPct - hBenchPct : undefined;
  const hValue = mode === '$' && hover ? formatCurrency(hover.close) : undefined;
  const hBenchValue = mode === '$' && hover?.bench !== undefined ? formatCurrency(hover.bench) : undefined;

  const clientW = containerRef.current?.clientWidth ?? 800;
  const clampLeft = (x: number) => Math.min(Math.max(x, 8), clientW - 348);

  return (
    <DataCard
      className="rounded-[14px] bg-[color:var(--nq-surface)] p-5 shadow-[var(--shadow-card)]"
      title="Portfolio candles"
      action={
        <div className="inline-flex rounded-md border border-border p-0.5">
          {(['$', '%'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onModeChange(m)}
              aria-pressed={m === mode}
              className={`cursor-pointer appearance-none rounded-[5px] border-0 px-2.5 py-0.5 text-xs transition-colors ${
                m === mode
                  ? 'bg-primary/10 text-primary'
                  : 'bg-transparent text-muted-foreground hover:text-foreground/65'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      }
    >
      {error && (
        <div className="mb-2 rounded-lg border border-[color:rgba(220,80,80,0.25)] bg-[color:rgba(220,80,80,0.12)] px-3 py-2 text-[13px] text-[color:var(--nq-down-soft)]">
          {error}
        </div>
      )}

      <div className="relative select-none">
        {loading && !hasData && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-sm text-muted-foreground">
            Loading chart…
          </div>
        )}
        {!loading && !hasData && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-sm text-muted-foreground">
            {error
              ? 'Candles are unavailable for this range.'
              : 'Candles will appear once performance history loads.'}
          </div>
        )}

        {hover && hasData && (
          <TvTooltipCard style={{ left: clampLeft(hover.x + 14), top: Math.max(hover.y - 8, 8) }}>
            <p className="mb-1.5 text-xs text-muted-foreground">{fmtTvDate(hover.time)}</p>
            <TvRow
              label="NovaQuantis Portfolio"
              value={hValue}
              pct={fmtPct(hPct)}
              pctClass={toneClass(hPct)}
              strong
            />
            {benchmarkAvailable && (
              <TvRow
                label="S&P 500"
                value={hBenchValue}
                pct={hBenchPct !== undefined ? fmtPct(hBenchPct) : '—'}
                pctClass={toneClass(hBenchPct)}
              />
            )}
            {hAlpha !== undefined && (
              <TvRow label="Alpha vs S&P 500" pct={`${fmtPct(hAlpha)} pts`} pctClass={toneClass(hAlpha)} bordered strong />
            )}
          </TvTooltipCard>
        )}

        <div ref={containerRef} className="h-[340px] w-full" />
      </div>
    </DataCard>
  );
};

export default PortfolioCandlesChart;
